import { useCallback, useState } from 'react';
import { LayoutChangeEvent, useWindowDimensions } from 'react-native';
import { Button, Heading, Layout, Pressable, Text } from '../../../../components/core';
import { IconBookmark } from '../../../../components/icons/IconBookmark';
import { IconBookmarkWhite } from '../../../../components/icons/IconBookmarkWhite';
import { IconExternalLink } from '../../../../components/icons/IconExternalLink';
import { useHeapContext } from '../../../../contexts/heapContext';
import { HubSurveyResultRecommendationArea, HubSurveyResultResource } from '../../../../graphQL';
import { useExternalNavigate } from '../../../../routes';
import { getStylesheet } from '../../../../styles';
import { BookmarkApi } from '../SurveyResultsPage';

export const RecommendationArea = ({
  recommendationArea,
  bookmarkApi,
}: {
  recommendationArea: HubSurveyResultRecommendationArea;
  bookmarkApi: BookmarkApi;
}): JSX.Element => {
  const { title, description, resources } = recommendationArea;
  const headerId = recommendationArea.recommendationArea;

  return (
    <Layout.VStack {...styles.recommendationArea} space={4} aria-labelledby={headerId}>
      <Heading.h4 id={headerId}>{title}</Heading.h4>

      <Text.para>{description}</Text.para>

      <Layout.VStack space={4} role="list">
        {resources.map(resource => (
          <ResourceCard
            key={resource.id}
            bookmarkApi={bookmarkApi}
            resource={resource}
            recommendationAreaId={recommendationArea.recommendationId}
          />
        ))}
      </Layout.VStack>
    </Layout.VStack>
  );
};

type ResourceCardProps = {
  resource: HubSurveyResultResource;
  recommendationAreaId?: number;
  bookmarkApi: BookmarkApi;
};

export const ResourceCard = ({
  resource,
  recommendationAreaId,
  bookmarkApi,
}: ResourceCardProps): JSX.Element => {
  const { trackEvent } = useHeapContext();
  const externalNavigate = useExternalNavigate();
  const { name, description, resourceType, website } = resource;
  const bookmarked = bookmarkApi.isBookmarked(resource.id);
  const headerId = `resource-${resource.id}`;

  // Need special controls for the smallest screen sizes
  const { width: screenWidth } = useWindowDimensions();
  const isMobileSmall = screenWidth < 360;

  const onViewResourcePress = useCallback((): void => {
    trackEvent('survey_resource_click', {
      assessmentRecommendationId: recommendationAreaId,
      organizationResourceId: resource.id,
    });
    externalNavigate(resource.website ?? '');
  }, [trackEvent, externalNavigate, resource.id, resource.website, recommendationAreaId]);

  const onBookmarkPress = async (): Promise<void> => {
    await bookmarkApi.toggle({ id: resource.id, name: resource.name, recommendationAreaId });
  };

  return (
    <Layout.Shift
      {...styles.resourceCard}
      alignNonMobile="center"
      role="listitem"
      aria-labelledby={headerId}
    >
      <Layout.VStack space={2} flex={1} maxWidth="100%">
        <Heading.h5 id={headerId}>{name}</Heading.h5>
        <ResourceDescription description={description ?? ''} />
      </Layout.VStack>

      <Layout.Shift flip={!isMobileSmall} {...styles.buttonGroup}>
        <Layout.Box {...styles.fixedBookmarkButtonContainer}>
          <Button.primaryMedium
            testID={`bookmark-resource-${recommendationAreaId}-${resourceType}`}
            onPress={onBookmarkPress}
            leftIcon={bookmarked ? <IconBookmarkWhite aria-hidden /> : <IconBookmark aria-hidden />}
          >
            {bookmarked ? 'Saved' : 'Save'}
          </Button.primaryMedium>
        </Layout.Box>
        {website != null && website !== '' && (
          <Button.secondaryMedium
            testID={`view-resource-${recommendationAreaId}-${resourceType}`}
            isLink
            onPress={onViewResourcePress}
            leftIcon={<IconExternalLink aria-hidden />}
          >
            View
          </Button.secondaryMedium>
        )}
      </Layout.Shift>
    </Layout.Shift>
  );
};

const ResourceDescription = ({ description }: { description: string }): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const LINE_HEIGHT = 20;
  const MAX_LINES = 4;

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (height > LINE_HEIGHT * MAX_LINES) {
      setShowReadMore(true);
    }
  }, []);

  const toggleExpanded = useCallback((): void => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <Layout.View>
      <Text.para
        noOfLines={showReadMore && !isExpanded ? 4 : undefined}
        isTruncated={showReadMore && !isExpanded}
        onLayout={onLayout}
      >
        {description}
      </Text.para>
      {showReadMore && (
        <Pressable
          aria-expanded={isExpanded}
          accessibilityState={{ expanded: isExpanded }}
          role="button"
          accessibilityRole="button"
          onPress={toggleExpanded}
          testID={`toggle-read-${isExpanded ? 'less' : 'more'}`}
        >
          <Text.para color="primary.600">{isExpanded ? 'Read less' : 'Read more'}</Text.para>
        </Pressable>
      )}
    </Layout.View>
  );
};

const styles = getStylesheet({
  recommendationArea: {
    backgroundColor: 'white',
    borderColor: 'secondary.300',
    borderWidth: 1,
    borderRadius: 16,
    padding: 8,
  },

  resourceCard: {
    alignItems: 'flex-start',
    backgroundColor: 'secondary.50',
    borderColor: 'secondary.300',
    borderWidth: 1,
    borderRadius: 10,
    gap: 4,
    padding: 6,
  },

  resourceDescription: {
    flex: 1,
  },
  fixedBookmarkButtonContainer: {
    width: 110,
  },
  buttonGroup: {
    gap: 4,
  },
});
